import { createContext, use, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

const STORAGE_KEY = "lytra_selected_tenant_id";
export const TENANT_PARAM = "tenant";

interface ImpersonationContextType {
  /** The impersonated tenant's slug, or its id when it has no slug. */
  impersonateTenant: string | undefined;
  setImpersonateTenant: (tenant: string | undefined) => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

/**
 * Provider that manages impersonation state ABOVE Apollo Client.
 * This allows the impersonated tenant to be included in the X-Impersonate-Tenant header
 * for all GraphQL requests.
 *
 * The tenant lives in the URL (`?tenant=<slug>`, #733), so each tab can work on its own
 * tenant and a link opens the tenant it was copied from. localStorage only remembers the
 * last tenant, as the default for a URL that names none.
 *
 * Only staff impersonate. Anyone else ignores both from the first render, e.g. when
 * opening a link a staff member shared: sending the tenant and dropping it later would
 * reset the Apollo store under the page for nothing.
 */
export function ImpersonationProvider({
  isLytraStaff,
  children,
}: {
  isLytraStaff: boolean;
  children: ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const impersonateTenant = isLytraStaff
    ? (searchParams.get(TENANT_PARAM) ?? localStorage.getItem(STORAGE_KEY) ?? undefined)
    : undefined;

  const updateImpersonateTenant = (tenant: string | undefined) => {
    if (tenant) {
      localStorage.setItem(STORAGE_KEY, tenant);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    setSearchParams(
      (params) => {
        if (tenant) {
          params.set(TENANT_PARAM, tenant);
        } else {
          params.delete(TENANT_PARAM);
        }
        return params;
      },
      { replace: true },
    );
  };

  return (
    <ImpersonationContext
      value={{
        impersonateTenant,
        setImpersonateTenant: updateImpersonateTenant,
      }}
    >
      {children}
    </ImpersonationContext>
  );
}

/** `?tenant=<slug>` for a URL opened outside the router (window.open), or "" for none. */
export function useTenantSearch() {
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get(TENANT_PARAM);
  return tenant ? `?${new URLSearchParams({ [TENANT_PARAM]: tenant })}` : "";
}

export function useImpersonation() {
  const context = use(ImpersonationContext);
  if (context === undefined) {
    throw new Error("useImpersonation must be used within an ImpersonationProvider");
  }
  return context;
}

/**
 * Consumer component for accessing impersonation context in render props pattern.
 * Useful when you need to pass the impersonated tenant to a component prop.
 */
export function ImpersonationConsumer({
  children,
}: {
  children: (context: ImpersonationContextType) => ReactNode;
}) {
  const context = useImpersonation();
  return <>{children(context)}</>;
}
